import React, { useState, useEffect, useRef } from 'react';

const UpdateSummary = () => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isNewDataAvailable, setIsNewDataAvailable] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Initial fetch
    fetchAISummary();

    // Set up auto-refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchAISummary(true); // Pass true to indicate this is a background refresh
    }, 30000);

    // Cleanup interval on component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Also refresh when the page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAISummary(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchAISummary = async (isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner for initial load, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      const token = localStorage.getItem('token');
      const response = await fetch('/api/ai-summary/latest', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI summary data');
      }

      const result = await response.json();

      // Only update if we got new data or it's the first load
      if (!summaryData || !summaryData.date || new Date(result.data.date) > new Date(summaryData.date)) {
        setSummaryData(result.data);
        setLastFetchTime(new Date());

        // Show a subtle notification for background updates
        if (isBackgroundRefresh && summaryData) {
          setIsNewDataAvailable(true);
          console.log('AI Summary updated with latest data');

          // Hide the notification after 3 seconds
          setTimeout(() => {
            setIsNewDataAvailable(false);
          }, 3000);
        }
      }

      setError(null);
    } catch (err) {
      // Only set error state for initial loads, not background refreshes
      if (!isBackgroundRefresh) {
        setError(err.message);
      }
      console.error('Error fetching AI summary:', err);
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  };





  return (
    <div className="flex-1 bg-slate-950 text-white overflow-auto">
      <div className="p-6">
        {/* New data notification */}
        {isNewDataAvailable && (
          <div className="mb-4 p-3 bg-green-600 text-white rounded-lg flex items-center gap-2 animate-pulse">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            AI Summary updated with latest data!
          </div>
        )}

        {/* Header with refresh controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Project Update Summary</h1>
            {lastFetchTime && (
              <p className="text-sm text-slate-400 mt-1">
                Last updated: {lastFetchTime.toLocaleString()} • Auto-refreshes every 30 seconds
              </p>
            )}
          </div>
          <button
            onClick={() => fetchAISummary()}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Overview Cards */}
          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              Work In Progress
            </h3>
            <div className="text-slate-300">
              {loading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                </div>
              ) : error ? (
                <p className="text-red-400">Error loading data</p>
              ) : summaryData?.content?.work_in_progress ? (
                summaryData.content.work_in_progress.bullets.map((bullet, index) => (
                  <p key={index} className="mb-2">• {bullet.summary}</p>
                ))
              ) : (
                <p className="text-slate-500">No data available</p>
              )}
            </div>
            {summaryData?.content?.work_in_progress?.summary && (
              <div className="mt-4 text-sm text-slate-400 bg-slate-900 p-3 rounded">
                {summaryData.content.work_in_progress.summary}
              </div>
            )}
          </div>

          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              Recently Completed
            </h3>
            <div className="text-slate-300">
              {loading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                </div>
              ) : error ? (
                <p className="text-red-400">Error loading data</p>
              ) : summaryData?.content?.recently_completed ? (
                summaryData.content.recently_completed.bullets.map((bullet, index) => (
                  <p key={index} className="mb-2">• {bullet.summary}</p>
                ))
              ) : (
                <p className="text-slate-500">No data available</p>
              )}
            </div>
            {summaryData?.content?.recently_completed?.summary && (
              <div className="mt-4 text-sm text-slate-400 bg-slate-900 p-3 rounded">
                {summaryData.content.recently_completed.summary}
              </div>
            )}
          </div>

          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
              Pending Work
            </h3>
            <div className="text-slate-300">
              {loading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                </div>
              ) : error ? (
                <p className="text-red-400">Error loading data</p>
              ) : summaryData?.content?.pending_work ? (
                summaryData.content.pending_work.bullets.map((bullet, index) => (
                  <p key={index} className="mb-2">• {bullet.summary}</p>
                ))
              ) : (
                <p className="text-slate-500">No data available</p>
              )}
            </div>
            {summaryData?.content?.pending_work?.summary && (
              <div className="mt-4 text-sm text-slate-400 bg-slate-900 p-3 rounded">
                {summaryData.content.pending_work.summary}
              </div>
            )}
          </div>

          <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
              Resource Overview
            </h3>
            <div className="text-slate-300">
              {loading ? (
                <div className="space-y-2">
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                  <div className="h-4 bg-slate-600 rounded animate-pulse"></div>
                </div>
              ) : error ? (
                <p className="text-red-400">Error loading data</p>
              ) : summaryData?.content?.resource_overview ? (
                summaryData.content.resource_overview.bullets.map((bullet, index) => (
                  <p key={index} className="mb-2">• {bullet.summary}</p>
                ))
              ) : (
                <p className="text-slate-500">No data available</p>
              )}
            </div>
            {summaryData?.content?.resource_overview?.summary && (
              <div className="mt-4 text-sm text-slate-400 bg-slate-900 p-3 rounded">
                {summaryData.content.resource_overview.summary}
              </div>
            )}
          </div>
        </div>

        {/* Detailed Summary Section */}
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Executive Summary</h3>
          <div className="bg-slate-900 rounded-lg p-4 border border-slate-600">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-slate-400 text-sm">Loading AI-powered project summary...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <p className="text-red-400 text-sm">Error loading executive summary</p>
                  <button
                    onClick={fetchAISummary}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : summaryData?.content?.executive_summary ? (
              <div className="text-slate-300">
                <p className="leading-relaxed">{summaryData.content.executive_summary.summary}</p>
                {summaryData.date && (
                  <div className="mt-4 pt-4 border-t border-slate-700 text-xs text-slate-500">
                    Last updated: {new Date(summaryData.date).toLocaleString()}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <p className="text-slate-400 text-sm">No executive summary available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateSummary;